/**
 * Accessibility utilities for WCAG 2.1 AA compliance
 */

// Color contrast utilities
export const colorContrast = {
  // WCAG AA minimum contrast ratios
  NORMAL_TEXT: 4.5,
  LARGE_TEXT: 3.0,
  
  /**
   * Calculate relative luminance of a color
   * @param r Red component (0-255)
   * @param g Green component (0-255)
   * @param b Blue component (0-255)
   */
  getLuminance(r: number, g: number, b: number): number {
    const [rs, gs, bs] = [r, g, b].map(c => {
      c = c / 255
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
  },

  /**
   * Calculate contrast ratio between two colors
   * @param color1 First color in hex format (#RRGGBB)
   * @param color2 Second color in hex format (#RRGGBB)
   */
  getContrastRatio(color1: string, color2: string): number {
    const hex1 = color1.replace('#', '')
    const hex2 = color2.replace('#', '')
    
    const r1 = parseInt(hex1.substr(0, 2), 16)
    const g1 = parseInt(hex1.substr(2, 2), 16)
    const b1 = parseInt(hex1.substr(4, 2), 16)
    
    const r2 = parseInt(hex2.substr(0, 2), 16)
    const g2 = parseInt(hex2.substr(2, 2), 16)
    const b2 = parseInt(hex2.substr(4, 2), 16)
    
    const lum1 = this.getLuminance(r1, g1, b1)
    const lum2 = this.getLuminance(r2, g2, b2)
    
    const brightest = Math.max(lum1, lum2)
    const darkest = Math.min(lum1, lum2)
    
    return (brightest + 0.05) / (darkest + 0.05)
  },

  /**
   * Check if color combination meets WCAG AA standards
   * @param foreground Foreground color in hex
   * @param background Background color in hex
   * @param isLargeText Whether the text is large (18pt+ or 14pt+ bold)
   */
  meetsWCAG(foreground: string, background: string, isLargeText = false): boolean {
    const ratio = this.getContrastRatio(foreground, background)
    const threshold = isLargeText ? this.LARGE_TEXT : this.NORMAL_TEXT
    return ratio >= threshold
  }
}

// Keyboard navigation utilities
export const keyboardNavigation = {
  /**
   * Handle keyboard navigation for interactive elements
   * @param event Keyboard event
   * @param callback Function to call on Enter or Space
   */
  handleActivation(event: React.KeyboardEvent, callback: () => void): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      callback()
    }
  },

  /**
   * Handle arrow key navigation in lists
   * @param event Keyboard event
   * @param currentIndex Current focused item index
   * @param totalItems Total number of items
   * @param onNavigate Callback with new index
   */
  handleArrowNavigation(
    event: React.KeyboardEvent,
    currentIndex: number,
    totalItems: number,
    onNavigate: (index: number) => void
  ): void {
    let newIndex = currentIndex

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault()
        newIndex = (currentIndex + 1) % totalItems
        break
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault()
        newIndex = currentIndex === 0 ? totalItems - 1 : currentIndex - 1
        break
      case 'Home':
        event.preventDefault()
        newIndex = 0
        break
      case 'End':
        event.preventDefault()
        newIndex = totalItems - 1
        break
      default:
        return
    }

    onNavigate(newIndex)
  }
}

// Screen reader utilities
export const screenReader = {
  /**
   * Generate accessible label for service cards
   * @param serviceName Name of the service
   * @param isComingSoon Whether the service is coming soon
   * @param pricing Pricing information
   */
  getServiceCardLabel(serviceName: string, isComingSoon: boolean, pricing: string): string {
    const status = isComingSoon ? 'Coming soon' : 'Available now'
    return `${serviceName} service. ${status}. ${pricing}. Click to learn more.`
  },

  /**
   * Generate accessible description for navigation items
   * @param itemName Name of the navigation item
   * @param isActive Whether the item is currently active
   */
  getNavItemDescription(itemName: string, isActive: boolean): string {
    return isActive ? `${itemName}, current page` : `Navigate to ${itemName}`
  },

  /**
   * Generate live region announcements
   * @param message Message to announce
   * @param priority Priority level (polite or assertive)
   */
  announce(message: string, priority: 'polite' | 'assertive' = 'polite'): void {
    const announcement = document.createElement('div')
    announcement.setAttribute('aria-live', priority)
    announcement.setAttribute('aria-atomic', 'true')
    announcement.className = 'sr-only'
    announcement.textContent = message
    
    document.body.appendChild(announcement)
    
    // Remove after announcement
    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)
  }
}

// Focus management utilities
export const focusManagement = {
  /**
   * Trap focus within a container (for modals, menus)
   * @param container Container element
   * @param event Keyboard event
   */
  trapFocus(container: HTMLElement, event: KeyboardEvent): void {
    if (event.key !== 'Tab') return

    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0] as HTMLElement
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  },

  /**
   * Set focus to first focusable element in container
   * @param container Container element
   */
  focusFirst(container: HTMLElement): void {
    const focusableElement = container.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ) as HTMLElement
    
    if (focusableElement) {
      focusableElement.focus()
    }
  },

  /**
   * Create visible focus indicator styles
   */
  getFocusStyles(): string {
    return `
      outline: 2px solid #2563eb;
      outline-offset: 2px;
      border-radius: 4px;
    `
  }
}

// Reduced motion utilities
export const reducedMotion = {
  /**
   * Check if user prefers reduced motion
   */
  prefersReducedMotion(): boolean {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  },

  /**
   * Get animation duration based on user preference
   * @param normalDuration Normal animation duration
   * @param reducedDuration Reduced animation duration
   */
  getDuration(normalDuration: number, reducedDuration: number = 0): number {
    return this.prefersReducedMotion() ? reducedDuration : normalDuration
  },

  /**
   * Get animation configuration respecting user preferences
   * @param animation Normal animation configuration
   */
  getAnimation(animation: any): any {
    if (this.prefersReducedMotion()) {
      return {
        ...animation,
        duration: 0,
        transition: { duration: 0 }
      }
    }
    return animation
  }
}

// Touch target utilities
export const touchTargets = {
  /**
   * Minimum touch target size (44px as per WCAG)
   */
  MIN_SIZE: 44,

  /**
   * Check if element meets minimum touch target size
   * @param element HTML element to check
   */
  meetsMinimumSize(element: HTMLElement): boolean {
    const rect = element.getBoundingClientRect()
    return rect.width >= this.MIN_SIZE && rect.height >= this.MIN_SIZE
  },

  /**
   * Get CSS classes for minimum touch target
   */
  getMinTouchTargetClasses(): string {
    return 'min-w-[44px] min-h-[44px] flex items-center justify-center'
  }
}